import { Camera, CameraDirection } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
const PHOTO_WIDTH = 800;
const PHOTO_HEIGHT = 600;
const STRIP_MIME_PREFIX = /^data:image\/\w+;base64,/;
/**
 * Opens the front-facing camera stream via getUserMedia.
 * Rejects with a descriptive error when called over HTTP (mediaDevices unavailable in non-secure contexts).
 *
 * @returns A promise resolving to the MediaStream for the caller to assign to a video element.
 */
export const getWebCameraStream = () => {
    var _a;
    if (!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia)) {
        return Promise.reject(new Error('Camera is not available. Make sure the app is served over HTTPS or localhost.'));
    }
    return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
};
/**
 * Opens the native front-facing camera and returns a raw base64 PNG string at 800×600.
 *
 * @returns A promise resolving to a raw base64 PNG string (no MIME prefix).
 */
export const captureNativePhoto = async () => {
    const { uri } = await Camera.takePhoto({
        quality: 85,
        cameraDirection: CameraDirection.Front,
        correctOrientation: true,
    });
    if (!uri) {
        throw new Error('Camera.takePhoto() did not return a uri');
    }
    // Read the file through the native bridge rather than loading Camera's webPath as an <img> src:
    // webPath only resolves via Capacitor's file-scheme interception, which requires the WebView
    // itself to be served from capacitor://localhost. Under live reload (served from an external
    // dev server) that interception doesn't apply, and the image silently fails to load.
    const { data } = await Filesystem.readFile({ path: uri });
    if (typeof data !== 'string') {
        throw new Error('Filesystem.readFile() did not return a base64 string');
    }
    const canvas = await loadImageUrlToCanvas(`data:image/jpeg;base64,${data}`);
    return toPngBase64(resizeToPhotoSize(canvas));
};
/**
 * Captures the current frame from a web video element as a raw base64 PNG string at 800×600.
 *
 * @param videoElement - The live <video> element showing the getUserMedia stream.
 * @returns A raw base64 PNG string (no MIME prefix).
 */
export const captureWebPhoto = (videoElement) => {
    var _a;
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    (_a = canvas.getContext('2d')) === null || _a === void 0 ? void 0 : _a.drawImage(videoElement, 0, 0);
    return toPngBase64(resizeToPhotoSize(canvas));
};
// ---------------------------------------------------------------------------
/**
 * Resizes and center-crops a canvas to exactly 800×600 using a cover strategy:
 * scales the source to fill the target, then crops any overflow from the edges.
 */
const resizeToPhotoSize = (source) => {
    const out = document.createElement('canvas');
    out.width = PHOTO_WIDTH;
    out.height = PHOTO_HEIGHT;
    const ctx = out.getContext('2d');
    if (!ctx) {
        return out;
    }
    const scale = Math.max(PHOTO_WIDTH / source.width, PHOTO_HEIGHT / source.height);
    const w = source.width * scale;
    const h = source.height * scale;
    ctx.drawImage(source, (PHOTO_WIDTH - w) / 2, (PHOTO_HEIGHT - h) / 2, w, h);
    return out;
};
const toPngBase64 = (canvas) => canvas.toDataURL('image/png').replace(STRIP_MIME_PREFIX, '');
const loadImageUrlToCanvas = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        var _a;
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        (_a = canvas.getContext('2d')) === null || _a === void 0 ? void 0 : _a.drawImage(img, 0, 0);
        resolve(canvas);
    };
    img.onerror = reject;
    img.src = url;
});
